import { createContext, useEffect, useState } from "react";
import { auth, db } from "../firebase";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc, serverTimestamp, updateDoc } from "firebase/firestore";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setLoading(true);

      try {
        if (currentUser) {
          setUser(currentUser);

          const docRef = doc(db, "users", currentUser.uid);
          const snap = await getDoc(docRef);

          if (snap.exists()) {
            const data = snap.data();
            const profile = data.profile || {};
            const onboarding = data.onboarding || {};

            const normalizedUserData = {
              ...data,
              isActive: data.isActive !== false,
              onboardingCompleted: data.onboardingCompleted !== false,
              profileCompleted:
                data.profileCompleted ??
                Boolean(data.fullName && data.email && (profile.phone || data.phone)),
              goal: data.goal || onboarding.goal || "",
              experienceLevel: data.experienceLevel || onboarding.level || "",
              interestedTrack: data.interestedTrack || onboarding.interest || "",
              courseInterest: data.courseInterest || onboarding.interest || "",
            };

            setUserData(normalizedUserData);
            setRole(normalizedUserData.role || "student");

            updateDoc(docRef, {
              lastActiveAt: serverTimestamp(),
            }).catch((error) => {
              console.warn("Unable to update last active timestamp:", error);
            });
          } else {
            setUserData(null);
            setRole(null);
          }
        } else {
          setUser(null);
          setUserData(null);
          setRole(null);
        }
      } catch (error) {
        console.error("Unable to load auth context:", error);
        setRole(null);
        setUserData(null);
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const authState = {
    user,
    role,
    userData,
    loading,
    isActive: userData?.isActive !== false,
    onboardingCompleted: userData?.onboardingCompleted !== false,
    profileCompleted: Boolean(userData?.profileCompleted),
    goal: userData?.goal || "",
    experienceLevel: userData?.experienceLevel || "",
    interestedTrack: userData?.interestedTrack || "",
    courseInterest: userData?.courseInterest || "",
  };

  return (
    <AuthContext.Provider value={authState}>
      {children}
    </AuthContext.Provider>
  );
};
